import React from 'react';
import { Link } from 'react-router-dom';
import { useDonationsAvailable } from '../hooks/useDonationsAvailable';

interface DonateButtonProps {
  /** Label shown once donations are open. */
  readonly label: string;
  /** Classes for the button itself, so each placement keeps its own sizing. */
  readonly className: string;
  /** Classes for the wrapping element (layout/visibility), kept identical in both states. */
  readonly wrapperClassName?: string;
  /** Fires on navigate — used by the mobile menu to close itself. */
  readonly onNavigate?: () => void;
}

/**
 * Donate call-to-action, gated on the backend being reachable. Until it is, the button is
 * inert and says so, rather than sending someone into a checkout that cannot complete.
 * While the first probe is in flight it stays disabled under its normal label, so the copy
 * does not flicker on a healthy load.
 */
const DonateButton: React.FC<DonateButtonProps> = ({ label, className, wrapperClassName, onNavigate }) => {
  const status = useDonationsAvailable();

  if (status === 'available') {
    return (
      <Link to="/donate" className={wrapperClassName} onClick={onNavigate}>
        <button className={className}>{label}</button>
      </Link>
    );
  }

  const pending = status === 'checking';
  return (
    <div className={wrapperClassName}>
      <button
        className={`${className} opacity-60 cursor-not-allowed`}
        disabled
        aria-disabled="true"
        title={pending ? 'Checking availability…' : 'Donations are not open yet — check back soon.'}
      >
        {pending ? label : 'Donations Coming Soon'}
      </button>
    </div>
  );
};

export default DonateButton;
